import React, { useCallback, useState } from 'react';
import { Copy, ChevronDown, ChevronUp, Code2, Table2, Check } from 'lucide-react';

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-CL', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

/** Aplica **negrita** a un segmento de texto */
const renderInline = (text) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, j) =>
    j % 2 === 1 ? (
      <strong key={j} className="font-semibold text-slate-900 dark:text-slate-100">
        {part}
      </strong>
    ) : (
      part
    )
  );

/** Renderiza texto con **negrita**, listas (- / * / 1.) y saltos de línea */
const renderContent = (content) => {
  if (!content) return null;
  const lines = content.split('\n');
  const result = [];
  let listItems = [];
  let listType = null;

  const flushList = (key) => {
    if (!listItems.length) return;
    const Tag = listType;
    const cls = Tag === 'ul'
      ? 'list-disc pl-5 space-y-0.5 my-1'
      : 'list-decimal pl-5 space-y-0.5 my-1';
    result.push(<Tag key={`list-${key}`} className={cls}>{listItems}</Tag>);
    listItems = [];
    listType = null;
  };

  lines.forEach((line, i) => {
    const ulMatch = line.match(/^[-*]\s+(.+)/);
    const olMatch = line.match(/^\d+\.\s+(.+)/);

    if (ulMatch) {
      if (listType === 'ol') flushList(i);
      listType = 'ul';
      listItems.push(<li key={i} className="leading-relaxed">{renderInline(ulMatch[1])}</li>);
    } else if (olMatch) {
      if (listType === 'ul') flushList(i);
      listType = 'ol';
      listItems.push(<li key={i} className="leading-relaxed">{renderInline(olMatch[1])}</li>);
    } else {
      flushList(i);
      if (line === '') {
        result.push(<br key={i} />);
      } else {
        result.push(
          <span key={i} className="block leading-relaxed">{renderInline(line)}</span>
        );
      }
    }
  });

  flushList('end');
  return result;
};

/** Acordeón para mostrar SQL o muestra de datos */
const SourceAccordion = ({ source }) => {
  const [expanded, setExpanded] = useState(false);
  const isSql = source?.kind === 'sql';
  const Icon = isSql ? Code2 : Table2;

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200/80 bg-slate-50/80 dark:border-slate-700/60 dark:bg-slate-800/60">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-100/60 dark:hover:bg-slate-700/40 transition-colors"
      >
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" />
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {source?.title}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-slate-200/70 px-3 py-2.5 dark:border-slate-700/50">
          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-slate-700 dark:text-slate-300">
            {source?.excerpt || ''}
          </pre>
        </div>
      )}
    </div>
  );
};

/** Indicador de "pensando" con puntos animados */
const ThinkingDots = () => (
  <div className="flex items-center gap-1.5 py-1">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="h-2 w-2 rounded-full bg-[#007D6E]/50 animate-bounce"
        style={{ animationDelay: `${i * 180}ms` }}
      />
    ))}
  </div>
);

const MessageBubble = ({ message, isPending }) => {
  const isAssistant = message?.role === 'assistant';
  const sources = Array.isArray(message?.sources) ? message.sources : [];
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const content = String(message?.content || '');
    if (!content) return;
    try {
      await navigator?.clipboard?.writeText?.(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }, [message]);

  /* ── Mensaje del asistente ────────────────────────────── */
  if (isAssistant) {
    return (
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#007D6E]/10 dark:bg-[#007D6E]/20 mt-0.5">
          <span className="text-[9px] font-bold tracking-tight text-[#007D6E]">AI</span>
        </div>

        {/* Burbuja */}
        <div className="min-w-0 flex-1">
          <div className="rounded-2xl rounded-tl-md border border-slate-200/60 bg-white px-4 py-3 shadow-sm dark:border-slate-700/60 dark:bg-slate-800">
            {isPending ? (
              <ThinkingDots />
            ) : (
              <>
                <div className="text-sm text-slate-700 dark:text-slate-200 space-y-0.5">
                  {renderContent(String(message?.content || ''))}
                </div>

                {sources.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {sources.map((src) => (
                      <SourceAccordion key={src?.id} source={src} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Timestamp + copy */}
          <div className="mt-1 flex items-center gap-2 px-1">
            <span className="text-[10px] text-slate-400">{formatTime(message?.createdAt)}</span>
            {!isPending && message?.content && (
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-300"
              >
                {copied ? (
                  <Check className="h-3 w-3 text-[#007D6E]" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
                {copied ? 'Copiado' : 'Copiar'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ── Mensaje del usuario ──────────────────────────────── */
  return (
    <div className="flex justify-end">
      <div className="max-w-[80%]">
        <div className="rounded-2xl rounded-tr-md bg-[#007D6E] px-4 py-3 shadow-sm">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-white">
            {String(message?.content || '')}
          </p>
        </div>
        <div className="mt-1 flex justify-end px-1">
          <span className="text-[10px] text-slate-400">{formatTime(message?.createdAt)}</span>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
